import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WheelPicker, WheelPickerWrapper } from "@ncdai/react-wheel-picker";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { DRIVER_AGES } from "@/schemas/carSearchSchema";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import BottomSheetTooltip from "@/components/ui/BottomSheetTooltip";

export default function DriverAgeSelect({ onSelect, value, scrollToForm }) {
  const t = useTranslationDetector();
  const isMobile = useIsMobile();
  const [shouldRenderPortal, setShouldRenderPortal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isWebDropdownAnimating, setIsWebDropdownAnimating] = useState(false);
  const [age, setAge] = useState(value);
  const [shouldRenderWebDropdown, setShouldRenderWebDropdown] = useState(false);
  const escapePressed = useKeyPressed("Escape");
  const ref = useRef(null);

  const handleClickOutside = useCallback(() => {
    // Mobilde portal açıksa click outside'ı devre dışı bırak
    if (isMobile && shouldRenderPortal) {
      return;
    }

    // Sadece web'de ve portal açık değilken çalıştır
    if (!isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen, shouldRenderPortal]);

  useOnClickOutside(ref, handleClickOutside);

  const handleClick = () => {
    setIsOpen(true);
  };

  const ageOptions = useMemo(() => {
    return DRIVER_AGES.map((ageItem) => ({
      value: ageItem,
      label: ageItem,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DRIVER_AGES]);

  const portalContainerClasses = useMemo(() => {
    return clsx(
      "fixed inset-0 z-50 flex items-end justify-center bg-black/50",
      "transform transition-all duration-300",
      isAnimating ? "opacity-100" : "opacity-0",
    );
  }, [isAnimating]);

  const portalContentClasses = useMemo(() => {
    return clsx(
      "w-full rounded-t-2xl bg-white",
      "transform transition-all duration-300",
      isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
    );
  }, [isAnimating]);

  const webDropdownClasses = useMemo(() => {
    return clsx(
      "web-dropdown shadow-xxl absolute top-full left-0 z-50 mt-2 w-auto rounded-lg bg-white",
      "origin-top transform transition-all duration-200 ease-out",
      isWebDropdownAnimating
        ? "translate-y-0 scale-100 opacity-100"
        : "-translate-y-2 scale-95 opacity-0",
    );
  }, [isWebDropdownAnimating]);

  const handlePortalClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleApplyMobile = useCallback(() => {
    onSelect?.(age);

    setIsOpen(false);
  }, [age, onSelect]);

  useEffect(() => {
    if (escapePressed && isOpen) {
      setIsOpen(false);
    }
  }, [escapePressed, isOpen]);

  useEffect(() => {
    if (isOpen && isMobile) {
      setAge(value);

      setShouldRenderPortal(true);
      const tO = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => clearTimeout(tO);
    } else if (!isOpen && isMobile) {
      setIsAnimating(false);
      const tO = setTimeout(() => {
        setShouldRenderPortal(false);
      }, 300);
      return () => clearTimeout(tO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (isOpen && !isMobile) {
      if (scrollToForm) {
        const inputRect = ref.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 290;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      // Web dropdown açılıyor

      setShouldRenderWebDropdown(true);
      // Küçük gecikme ile animasyonu tetikle
      const timer = setTimeout(() => {
        setIsWebDropdownAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen && !isMobile) {
      // Web dropdown kapanıyor
      setIsWebDropdownAnimating(false);
      // Animasyon bitimini bekle, sonra DOM'dan kaldır
      const timer = setTimeout(() => {
        setShouldRenderWebDropdown(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMobile]);

  const handleChangeMobile = useCallback((newVal) => {
    setAge(newVal);
  }, []);

  const handleChangeDesktop = useCallback((newVal) => {
    onSelect(newVal);
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderWheelPickers = useCallback(() => {
    const onValueChange = (val) => handleChangeMobile(val);

    return (
      <WheelPickerWrapper>
        <WheelPicker
          options={ageOptions}
          value={age}
          onValueChange={onValueChange}
          classNames={{
            optionItem: "text-foreground-muted !text-lg md:!text-base",
            highlightWrapper:
              "bg-background-300 rounded-tl-lg rounded-bl-lg !text-lg md:!text-base",
          }}
        />
      </WheelPickerWrapper>
    );
  }, [age, ageOptions, handleChangeMobile]);

  const renderWebDropdownContent = useCallback(() => {
    return (
      <ul className="scrollbar-default scrollbar-hover-only flex max-h-[256px] flex-col overflow-y-auto py-2">
        {ageOptions.map((ageItem) => (
          <li key={ageItem.value} className="w-full">
            <button
              className="w-full cursor-pointer px-4 py-[6px] text-left font-medium whitespace-nowrap hover:bg-[var(--color-primary-weaker)]"
              onClick={() => {
                handleChangeDesktop(ageItem.value);
              }}
            >
              {ageItem.label}
            </button>
          </li>
        ))}
      </ul>
    );
  }, [ageOptions, handleChangeDesktop]);

  return (
    <div className="relative">
      <div ref={ref} className="flex flex-row items-center gap-1">
        <div>{t("carSearch.driverAge")}</div>

        <div className="relative">
          <button
            onClick={handleClick}
            className="flex cursor-pointer items-center gap-1 font-semibold"
          >
            {value}

            <div className="flex h-4 w-4 items-center justify-center">
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["chevron-down-new"]}
                className="!w-4"
              />
            </div>
          </button>

          {/* Web dropdown */}
          {!isMobile && shouldRenderWebDropdown && (
            <div className={webDropdownClasses}>
              {renderWebDropdownContent()}
            </div>
          )}
        </div>

        <BottomSheetTooltip
          content={t("carSearch.driverAgeModalText")}
          bottomSheetTitle={t("carSearch.driverAgeModalTitle")}
          position={shouldRenderWebDropdown ? "top" : "bottom"}
          tooltipClassName="w-4 h-4 flex items-center justify-center"
        >
          <FontAwesomeIcon
            icon={byPrefixAndName.fak["circle-info-new"]}
            className="text-foreground-muted !w-4"
          />
        </BottomSheetTooltip>
      </div>

      {/* MOBILE PORTAL */}
      {isMobile &&
        shouldRenderPortal &&
        createPortal(
          <div className={portalContainerClasses} onClick={handlePortalClose}>
            <div
              className={portalContentClasses}
              onClick={(e) => e.stopPropagation()}
            >
              {/* header */}
              <div className="border-primary-weak relative flex items-center justify-between border-b py-2">
                <button
                  onClick={handlePortalClose}
                  className="!h-5 !w-5 pl-4 text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["xmark"]}
                    className="!h-5 !w-5"
                  />
                </button>

                <span className="absolute top-1/2 left-1/2 -translate-1/2 text-lg font-semibold">
                  {t("carSearch.driverAge")}
                </span>

                <div className="flex items-center">
                  <button
                    className="text-md px-3 py-2 font-medium text-[#2185f8]"
                    onClick={handleApplyMobile}
                  >
                    {t("common.apply")}
                  </button>
                </div>
              </div>

              {/* içerik */}
              <div className="w-full flex-col py-5">{renderWheelPickers()}</div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
